import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { translateFarmsError } from '../lib/translateError';
import type { Farm, FarmOrder } from '../types';

export interface FarmsHubData {
  farms:              Farm[];
  sellerOrders:       FarmOrder[];
  buyerOrders:        FarmOrder[];
  monthRevenueCents:  number;
  avgRating:          number;
  reviewsCount:       number;
}

const EMPTY: FarmsHubData = {
  farms:             [],
  sellerOrders:      [],
  buyerOrders:       [],
  monthRevenueCents: 0,
  avgRating:         0,
  reviewsCount:      0,
};

export function useFarmsHub() {
  const { tenantId } = useTenant();
  const [data, setData]     = useState<FarmsHubData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    const tid = tenantId; // narrowed: string (não null) para uso dentro da closure

    async function load() {
      setLoading(true);
      setError(null);
      try {
        // 1. Farms do tenant
        const { data: farms, error: farmsErr } = await supabase
          .from('farms')
          .select('id, owner_tenant_id, owner_user_id, name, slug, description, logo_url, status, created_at')
          .eq('owner_tenant_id', tid)
          .order('created_at', { ascending: false });
        if (farmsErr) throw new Error(farmsErr.message);

        const farmList = (farms ?? []) as Farm[];
        const farmIds  = farmList.map((f) => f.id);

        // 2. Pedidos do comprador (sempre) + dados de fornecedor (só se tem farms)
        const buyerRes = await supabase
          .from('farm_orders')
          .select('*')
          .eq('customer_tenant_id', tid)
          .order('created_at', { ascending: false })
          .limit(5);

        const buyerOrders = (buyerRes.data ?? []) as FarmOrder[];

        let sellerOrders: FarmOrder[]         = [];
        let reviews: { rating: number }[]     = [];

        if (farmIds.length > 0) {
          const [sellerRes, reviewsRes] = await Promise.all([
            supabase
              .from('farm_orders')
              .select('*')
              .in('farm_id', farmIds)
              .order('created_at', { ascending: false })
              .limit(5),
            supabase
              .from('farm_reviews')
              .select('rating')
              .in('farm_id', farmIds),
          ]);
          sellerOrders = (sellerRes.data  ?? []) as FarmOrder[];
          reviews      = (reviewsRes.data ?? []) as { rating: number }[];
        }

        // Receita do mês (pedidos concluídos como fornecedor)
        const monthStart = new Date();
        monthStart.setDate(1);
        monthStart.setHours(0, 0, 0, 0);

        const monthRevenueCents = sellerOrders
          .filter((o) => o.status === 'done' && new Date(o.created_at) >= monthStart)
          .reduce((sum, o) => sum + (o.supplier_amount_cents ?? 0), 0);

        // Avaliação média
        const ratings     = reviews.map((r) => r.rating);
        const avgRating   = ratings.length > 0
          ? ratings.reduce((a, b) => a + b, 0) / ratings.length
          : 0;

        setData({ farms: farmList, sellerOrders, buyerOrders, monthRevenueCents, avgRating, reviewsCount: ratings.length });
      } catch (e: any) {
        setError(translateFarmsError(e.message ?? ''));
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, [tenantId]);

  return { data, loading, error };
}
